import { createContext, useContext, useEffect, useState } from "react";
import { api, setAuthToken } from "@/api/client";
import { mistApi } from "@/api/mist";
import { vaultSecurityApi } from "@/api/vaultSecurity";
import { socialApi } from "@/api/social";

const SessionContext = createContext(null);
export const useSession = () => useContext(SessionContext);

const TOKEN_KEY = "mist_token";

export const TIER_META = {
  tier_1: { label: "MEMBER", kind: "normal" },
  tier_2: { label: "CREATOR", kind: "creator" },
  tier_3: { label: "BUSINESS", kind: "business" },
};

export function SessionProvider({ children }) {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [tier, setTier] = useState("tier_1");
  const [authed, setAuthed] = useState(null); // null = checking, false = gateway, true = app
  const [loading, setLoading] = useState(false);

  // Attach token + hydrate user/wallet WITHOUT flipping the gateway open.
  // STAGE 6 · PHASE 4 — passive gamification checkpoint (silent, best-effort).
  const trackGami = (event) => api.post("/gamification/track", { event }).catch(() => {});


  const applySession = async (accessToken, userObj) => {
    setAuthToken(accessToken);
    localStorage.setItem(TOKEN_KEY, accessToken);
    setToken(accessToken);
    setUser(userObj);
    setTier(userObj.subscription_tier || "tier_1");
    try {
      const { data } = await api.post("/wealth-money/invoke", { action: "check_wallet" });
      setWallet(data);
    } catch (e) {
      setWallet(null);
    }
    trackGami("login"); // passive daily-retention checkpoint (idempotent server-side)
  };

  const finishAuth = () => setAuthed(true);

  // ── Emergent-managed Google login ──────────────────────────────────────
  // Google is used for IDENTITY only; the backend returns the app's OWN JWT so
  // the rest of the platform (guards, wallet, BOLA) is untouched.
  const startGoogleLogin = () => {
    const redirect = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const signInWithGoogle = async (sessionId) => {
    const { data } = await api.post("/auth/google/session", { session_id: sessionId });
    await applySession(data.access_token, data.user);
    sessionStorage.setItem("mist_login_land", "command");
    finishAuth();
    return data;
  };

  // Presence heartbeat — keeps the user marked "online" in the social directory.
  useEffect(() => {
    if (authed !== true) return;
    let alive = true;
    const ping = () => { if (alive) socialApi.heartbeat().catch(() => {}); };
    ping();
    const id = setInterval(ping, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [authed]);

  // Restore an existing session on boot.
  useEffect(() => {
    (async () => {
      // 1) Google OAuth return — Emergent redirects back with #session_id=...
      const hash = window.location.hash || "";
      const m = hash.match(/session_id=([^&]+)/);
      if (m) {
        const sid = decodeURIComponent(m[1]);
        // Clean the hash immediately so a refresh can't re-trigger the exchange.
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
        try {
          await signInWithGoogle(sid);
          return;
        } catch (e) {
          // fall through to normal token restore / gateway
        }
      }
      const saved = localStorage.getItem(TOKEN_KEY);
      if (!saved) {
        setAuthed(false);
        return;
      }
      setAuthToken(saved);
      // Restore the session, retrying through transient gateway blips. A 520/524
      // (render pod briefly down/restarting), a 5xx, a network drop or a non-JSON
      // HTML error page must NEVER clear a valid token — that was the "logged out
      // mid-render" cascade. We ONLY sign the user out on a genuine auth rejection.
      let lastErr = null;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const { data } = await api.get("/auth/me");
          await applySession(saved, data.user);
          finishAuth();
          return;
        } catch (e) {
          lastErr = e;
          const st = e?.response?.status;
          if (st === 401 || st === 403) {          // real auth failure → sign out
            localStorage.removeItem(TOKEN_KEY);
            setAuthToken(null);
            setAuthed(false);
            return;
          }
          await new Promise((r) => setTimeout(r, 1500)); // transient → back off + retry
        }
      }
      // Still unreachable after retries: KEEP the token (a reload once the pod is
      // back restores the session with no re-login) and leave auth unresolved.
      void lastErr;
      setAuthed(false);
    })();
  }, []);

  const signIn = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    await applySession(data.access_token, data.user);
    sessionStorage.setItem("mist_login_land", "command");
    finishAuth();
    return data.user;
  };

  // Passwordless passkey / biometric login (additive to password login).
  const signInWithPasskey = async (username) => {
    const { loginWithPasskey } = await import("@/lib/passkey");
    const data = await loginWithPasskey(username);
    await applySession(data.access_token || data.token, data.user);
    sessionStorage.setItem("mist_login_land", "command");
    finishAuth();
    return data.user;
  };

  const signUp = async (payload) => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    const { data } = await api.post("/auth/signup", ref ? { ...payload, ref } : payload);
    return data; // { verification_required, email, dev_code }
  };

  const verifyCode = async (email, code) => {
    const { data } = await api.post("/auth/verify-code", { email, code });
    await applySession(data.access_token, data.user); // token live, gateway stays open for biometric
    return data.user;
  };

  const forgotPassword = async (email) => {
    const { data } = await api.post("/auth/forgot-password", { email });
    return data; // { ok, message, dev_code? }
  };

  const resetPassword = async (email, code, newPassword) => {
    const { data } = await api.post("/auth/reset-password", {
      email, code, new_password: newPassword,
    });
    return data; // { ok, reset }
  };

  const setBiometric = async (identityVerified, verificationMediaId) => {
    const payload = { identity_verified: identityVerified };
    if (verificationMediaId) payload.verification_media_id = verificationMediaId;
    const { data } = await api.post("/auth/set-biometric", payload);
    setUser(data.user);
    sessionStorage.setItem("mist_login_land", "command");
    finishAuth();
    return data.user;
  };

  const logout = () => {
    // Clean-slate logout: destroy the session, then hard-reset the browser to
    // the NAKED root origin so every deep-link param (?tab / ?hub / ?studio /
    // ?view / ?zone) is stripped. On next sign-in the app defaults to the
    // Command Centre dashboard. Full reload also clears all in-memory state.
    try {
      localStorage.removeItem(TOKEN_KEY);
      setAuthToken(null);
      sessionStorage.clear();
    } catch { /* storage may be unavailable — proceed to reset regardless */ }
    setToken(null);
    setUser(null);
    setWallet(null);
    setTier("tier_1");
    setAuthed(false);
    window.location.href = window.location.origin;
  };

  const switchTier = async (t) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/sandbox-session", { tier: t });
      await applySession(data.access_token, data.user);
      sessionStorage.setItem("mist_login_land", "command");
      finishAuth();
    } finally {
      setLoading(false);
    }
  };

  const refreshWallet = async () => {
    const { data } = await api.post("/wealth-money/invoke", { action: "check_wallet" });
    setWallet(data);
    return data;
  };

  // Stage 9 — clean session re-fetch (meta/mTokens) on dashboard remounts.
  const refreshUser = async () => {
    try { const { data } = await api.get("/auth/me"); setUser(data.user); return data.user; }
    catch { return null; }
  };

  // STAGE 6 · PHASE 2 — persist the module-profile ecosystem onto the user's
  // EXISTING users doc (device-agnostic; survives localStorage clears). No new
  // collection — reuses /social/profile-update.
  const saveModuleProfile = async (modules) => {
    const { user: updated } = await socialApi.updateProfile({ module_profile: modules });
    setUser((prev) => ({ ...prev, ...updated }));
    return updated;
  };

  // FUTURE-PROOF vault ingestion sinks — self-sorting by asset type. Exposed
  // via the secure context (and window) so ANY future macro/component can store
  // assets in the vault without new wiring. Each returns the stored asset record.
  const saveMp4ToVault = (file) => mistApi.ingest(file, "video").then((r) => { trackGami("vault_save"); return r; });
  const saveMp3ToVault = (file) => mistApi.ingest(file, "music").then((r) => { trackGami("vault_save"); return r; });
  const saveBrandAssetToVault = (file) => vaultSecurityApi.repoUpload("brand", file).then((r) => { trackGami("vault_save"); return r; });

  // C2 — decrypt the full BYOK registry in-memory on demand (never stored raw).
  const getKeys = async () => {
    const list = await vaultSecurityApi.byokRegistry();
    const out = {};
    for (const k of list.filter((x) => x.set)) {
      try { out[k.name] = await vaultSecurityApi.byokRegReveal(k.name); } catch { /* skip */ }
    }
    return out;
  };

  // Founder walkthrough → live Landing Page #1 pipe-back sink.
  const pushVideoToLanding = (assetId) => vaultSecurityApi.blueprintPushLanding(assetId);

  // Register the sinks on window so components mounted outside the provider tree
  // (future dynamically-injected macro tabs) can still route assets into the vault.
  useEffect(() => {
    window.MiStVaultSinks = { saveMp4ToVault, saveMp3ToVault, saveBrandAssetToVault, getKeys, pushVideoToLanding };
    return () => { delete window.MiStVaultSinks; };
  }, []);

  // Global coin-burn listener: every successful Mist Suite generation deducts 5.
  useEffect(() => {
    const onGen = () => {
      setWallet((w) => (w && typeof w.balance === "number" ? { ...w, balance: Math.max(0, w.balance - 5) } : w));
      refreshWallet().catch(() => {});
    };
    window.addEventListener("mist:generated", onGen);
    return () => window.removeEventListener("mist:generated", onGen);
  }, []);

  return (
    <SessionContext.Provider
      value={{
        token, user, wallet, tier, authed, loading,
        signIn, signInWithPasskey, signUp, verifyCode, forgotPassword, resetPassword, setBiometric, logout,
        startGoogleLogin, signInWithGoogle,
        switchTier, refreshWallet, refreshUser, setUser, setWallet,
        saveModuleProfile, trackGami,
        saveMp4ToVault, saveMp3ToVault, saveBrandAssetToVault,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}
